import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Card, Loading } from '../../../common';
import identityService from '../../../../services/identityService';
import './Charts.css';

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * MfaChart Component
 * Displays MFA status breakdown for human identities
 */
const MfaChart = () => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMfaData = async () => {
      setLoading(true);
      try {
        const response = await identityService.getMfaBreakdown();
        
        if (response?.success && response?.data) {
          // console.log('✅ MFA breakdown data received:', response.data);
          setChartData(response.data);
        } else {
          setError('Invalid response format');
        }
      } catch (err) {
        console.error('❌ Error fetching MFA breakdown:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMfaData();
  }, []);

  const buildChartData = () => {
    if (!chartData?.chartData) {
      return null;
    }

    const { labels, values } = chartData.chartData;

    return {
      labels: labels,
      datasets: [
        {
          data: values,
          backgroundColor: ['#28a745', '#dc3545'],
          borderWidth: 0,
          hoverOffset: 4
        }
      ]
    };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          
          font: {
            size: 12,
            family: "'Inter', sans-serif"
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(26, 26, 46, 0.9)',
        padding: 12,
        cornerRadius: 8
      }
    }
  };

  const data = buildChartData();
  const percentage = chartData?.chartData?.percentage || 0;

  return (
    <Card className="chart-card">
      <div className="chart-header">
        <h3 className="chart-title">MFA Status</h3>
        <span className="chart-subtitle">Human identities MFA enrollment</span>
      </div>
      <div className="chart-body">
        {loading ? (
          <div className="chart-loading">
            <Loading size="md" text="Loading MFA data..." />
          </div>
        ) : error ? (
          <div className="chart-error">
            <p>Failed to load MFA data</p>
            <small>{error}</small>
          </div>
        ) : data ? (
          <div className="chart-container pie-container">
            <Pie data={data} options={options} />
            <div className="mfa-percentage">
              <span className="percentage-value">{percentage}%</span>
              <span className="percentage-label">MFA Enabled</span>
            </div>
          </div>
        ) : (
          <div className="chart-empty">
            <p>No MFA data available</p>
          </div>
        )}
      </div>
      
      {/* MFA by User Type */}
      {chartData?.breakdown?.bySubType && (
        <div className="chart-footer">
          <div className="mfa-breakdown">
            <div className="mfa-breakdown-item">
              <span className="mfa-label">Users:</span>
              <span className="mfa-value enabled">{chartData.breakdown.bySubType.users.enabled}</span>
              <span className="mfa-separator">/</span>
              <span className="mfa-value disabled">{chartData.breakdown.bySubType.users.disabled}</span>
            </div>
            <div className="mfa-breakdown-item">
              <span className="mfa-label">Admins:</span>
              <span className="mfa-value enabled">{chartData.breakdown.bySubType.admins.enabled}</span>
              <span className="mfa-separator">/</span>
              <span className="mfa-value disabled">{chartData.breakdown.bySubType.admins.disabled}</span>
            </div>
            <div className="mfa-breakdown-item">
              <span className="mfa-label">Guests:</span>
              <span className="mfa-value enabled">{chartData.breakdown.bySubType.guests.enabled}</span>
              <span className="mfa-separator">/</span>
              <span className="mfa-value disabled">{chartData.breakdown.bySubType.guests.disabled}</span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default MfaChart;